import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Home, Settings, Eye } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const HeroSectionCRUD = ({ config, onUpdate }) => {
  const [heroContent, setHeroContent] = useState({
    logo_text: 'Ultralender.ai',
    title: 'AI Agents for the Mortgage Industry',
    subtitle: 'Discover powerful AI agents designed specifically for mortgage professionals. Zero cost to launch, outcome-based pricing that scales with your success.',
    cta_primary: 'Browse All AI Agents',
    cta_secondary: 'Request Custom Agent'
  });
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (config.hero && config.hero.content) {
      setHeroContent(config.hero.content);
    }
  }, [config]);

  const updateHeroContent = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_BASE_URL}/api/admin/config/hero/content`, {
        value: heroContent,
        data_type: 'json',
        description: 'Hero section content including title, subtitle, and CTA button text'
      }, {
        headers: getAuthHeaders()
      });
      
      alert('Hero section updated successfully!');
      onUpdate();
    } catch (error) {
      console.error('Error updating hero content:', error);
      if (error.response?.status === 401) {
        alert('Authentication required. Please log in again.');
      } else {
        alert('Error updating hero section');
      }
    }
    setLoading(false);
  };

  const resetToDefaults = () => {
    setHeroContent({
      logo_text: 'Ultralender.ai',
      title: 'AI Agents for the Mortgage Industry',
      subtitle: 'Discover powerful AI agents designed specifically for mortgage professionals. Zero cost to launch, outcome-based pricing that scales with your success.',
      cta_primary: 'Browse All AI Agents',
      cta_secondary: 'Request Custom Agent'
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <Home className="h-5 w-5 text-blue-600 mr-2" />
          <h4 className="text-lg font-semibold text-blue-900">Hero Section Content</h4>
        </div>
        <p className="text-blue-700 mt-2">
          Manage the main hero section content displayed on the homepage including title, subtitle, and call-to-action buttons.
        </p>
      </div>

      {/* Preview Toggle */}
      <div className="flex justify-between items-center">
        <h5 className="font-semibold text-gray-900">Content Editor</h5>
        <button
          onClick={() => setPreviewMode(!previewMode)}
          className="flex items-center space-x-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <Eye className="h-4 w-4" />
          <span>{previewMode ? 'Edit Mode' : 'Preview Mode'}</span>
        </button>
      </div>

      {previewMode ? (
        /* Preview Mode */
        <div className="bg-gradient-to-r from-primary-600 via-primary-500 to-primary-800 rounded-lg p-8">
          <div className="text-center text-white">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="h-8 w-8 bg-white rounded-full flex items-center justify-center">
                <span className="text-primary-600 font-bold text-lg">🤖</span>
              </div>
              <span className="text-2xl font-bold text-white">{heroContent.logo_text}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              {heroContent.title}
            </h1>
            <p className="text-lg text-blue-100 mb-6 max-w-3xl mx-auto">
              {heroContent.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold">
                {heroContent.cta_primary}
              </button>
              <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold">
                {heroContent.cta_secondary}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Edit Mode */
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo Text
            </label>
            <input
              type="text"
              value={heroContent.logo_text}
              onChange={(e) => setHeroContent({...heroContent, logo_text: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter logo text"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Company/brand name displayed in the hero section
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hero Title
            </label>
            <input
              type="text"
              value={heroContent.title}
              onChange={(e) => setHeroContent({...heroContent, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter hero section title"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Main headline displayed prominently on the homepage
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hero Subtitle
            </label>
            <textarea
              value={heroContent.subtitle}
              onChange={(e) => setHeroContent({...heroContent, subtitle: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={3}
              placeholder="Enter hero section subtitle/description"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Supporting text that describes your value proposition
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary CTA Button
              </label>
              <input
                type="text"
                value={heroContent.cta_primary}
                onChange={(e) => setHeroContent({...heroContent, cta_primary: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Primary button text"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Main call-to-action button (links to /browse)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secondary CTA Button
              </label>
              <input
                type="text"
                value={heroContent.cta_secondary}
                onChange={(e) => setHeroContent({...heroContent, cta_secondary: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Secondary button text"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Secondary call-to-action button (links to /custom)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Character Counts */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="font-semibold text-gray-900 mb-2">Content Statistics</h5>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Logo: </span>
            <span className={heroContent.logo_text.length > 20 ? 'text-red-600 font-medium' : 'text-green-600'}>
              {heroContent.logo_text.length}/20
            </span>
          </div>
          <div>
            <span className="text-gray-600">Title: </span>
            <span className={heroContent.title.length > 60 ? 'text-red-600 font-medium' : 'text-green-600'}>
              {heroContent.title.length}/60
            </span>
          </div>
          <div>
            <span className="text-gray-600">Subtitle: </span>
            <span className={heroContent.subtitle.length > 200 ? 'text-red-600 font-medium' : 'text-green-600'}>
              {heroContent.subtitle.length}/200
            </span>
          </div>
          <div>
            <span className="text-gray-600">CTA 1: </span>
            <span className={heroContent.cta_primary.length > 25 ? 'text-red-600 font-medium' : 'text-green-600'}>
              {heroContent.cta_primary.length}/25
            </span>
          </div>
          <div>
            <span className="text-gray-600">CTA 2: </span>
            <span className={heroContent.cta_secondary.length > 25 ? 'text-red-600 font-medium' : 'text-green-600'}>
              {heroContent.cta_secondary.length}/25
            </span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Recommended character limits for optimal display across devices
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-4 pt-4 border-t border-gray-200">
        <button
          onClick={updateHeroContent}
          disabled={loading}
          className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Settings className="h-4 w-4 mr-2" />
              Save Hero Content
            </>
          )}
        </button>
        
        <button
          onClick={resetToDefaults}
          disabled={loading}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reset to Defaults
        </button>
      </div>
    </div>
  );
};

export default HeroSectionCRUD;